import { useEffect, useState } from 'react';
import { AlertTriangle, Info } from 'lucide-react';
import { Modal } from '../ui/Modal';
import { Button } from '../ui/Button';
import { Input, Select, Textarea } from '../ui/Input';
import { tieneAsociacion } from '../../types/logbook';
import type { LogbookEntry, LogbookEntryType } from '../../types/logbook';
import type { Vehicle } from '../../types/vehicle';
import type { Driver } from '../../types/driver';
import type { LogbookEntryCreateDto, LogbookEntryPatchDto } from '../../lib/api/endpoints';
import { mensajeErrorNovedad } from '../../utils/logbook';
import { useLogbookAttachments } from '../../hooks/useLogbookAttachments';
import { LogbookPhotoPicker } from './LogbookPhotoPicker';
import { fromLocalInputValue, toLocalInputValue } from '../../utils/trips';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  /** Presente = CORREGIR esa novedad. Ausente = registrar una nueva. */
  entry?: LogbookEntry | null;
  tipos: LogbookEntryType[];
  vehicles: Vehicle[];
  drivers: Driver[];
  /** Preselección al registrar desde una superficie que ya sabe de qué cuelga. */
  vehicleIdInicial?: string;
  driverIdInicial?: string;
  tripIdInicial?: string;
  /** El Operador no elige de quién es la novedad: el servidor le estampa su conductor. */
  ocultarAsociaciones?: boolean;
  /**
   * Devuelve la novedad ya creada por el servidor. FE-30 **necesita su `id`**:
   * las fotos se suben a `/logbook-entries/{id}/attachments`, así que el
   * adjunto es una segunda llamada y no puede existir antes que la novedad.
   */
  onRegistrar: (data: LogbookEntryCreateDto) => Promise<LogbookEntry>;
  onCorregir: (entryId: string, data: LogbookEntryPatchDto) => Promise<unknown>;
}

interface FormState {
  entryTypeId: string;
  occurredAt: string;
  description: string;
  vehicleId: string;
  driverId: string;
  reason: string;
}

function ahoraLocal(): string {
  return toLocalInputValue(new Date().toISOString());
}

function desdeEntry(entry: LogbookEntry): FormState {
  return {
    entryTypeId: entry.entryTypeId,
    occurredAt: toLocalInputValue(entry.occurredAt),
    description: entry.description,
    vehicleId: entry.vehicleId ?? '',
    driverId: entry.driverId ?? '',
    reason: '',
  };
}

/**
 * FE-13 — registrar una novedad y corregirla son el MISMO formulario.
 *
 * Dos cosas de este formulario no son evidentes y las dos vienen del contrato:
 *
 *  1. **`occurredAt` por defecto es AHORA, pero se puede mover al pasado.** Es
 *     el campo que decide a qué día y a qué viaje pertenece la novedad
 *     (ADR-024). Una varada de anteayer relatada hoy tiene que poder declararse
 *     con su fecha real, o el expediente del viaje de anteayer nunca la verá.
 *
 *  2. **Quitar una asociación NO es mandarla vacía, es `unset`.** En JSON un
 *     campo ausente y uno en `null` llegan indistinguibles al backend, así que
 *     el contrato pide la intención explícita. Vaciar el selector de vehículo
 *     en modo corrección se traduce a `unset: ['vehicleId']`, no a `vehicleId:
 *     ''` — que el servidor rechazaría como UUID inválido.
 *
 * En modo corrección solo se envía lo que cambió: mandar el formulario entero
 * haría que cada corrección apareciera en la novedad tocando los cinco campos,
 * cuando quizá solo se ajustó una hora.
 */
export function LogbookEntryFormModal({
  isOpen, onClose, entry, tipos, vehicles, drivers,
  vehicleIdInicial, driverIdInicial, tripIdInicial, ocultarAsociaciones = false,
  onRegistrar, onCorregir,
}: Props) {
  const esCorreccion = !!entry;
  const [form, setForm] = useState<FormState>({
    entryTypeId: '', occurredAt: '', description: '', vehicleId: '', driverId: '', reason: '',
  });
  const [enviando, setEnviando] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // FE-30 — solo al REGISTRAR. En corrección las fotos se añaden desde el
  // detalle, igual que el recibo de un gasto (`ExpenseFormModal`): adjuntar es
  // añadir, no editar, y mezclarlo con el diff de la corrección confundiría
  // las dos cosas.
  const [fotos, setFotos] = useState<File[]>([]);
  const { subir, subiendo, progreso } = useLogbookAttachments(null);

  useEffect(() => {
    if (!isOpen) return;
    setError(null);
    setFotos([]);
    if (entry) {
      setForm(desdeEntry(entry));
      return;
    }
    setForm({
      // Sin preselección de tipo: elegir "Accidente" por ser el primero de la
      // lista sería poner una etiqueta que nadie decidió. El catálogo puede
      // además tener tipos propios del tenant en cualquier orden.
      entryTypeId: '',
      occurredAt: ahoraLocal(),
      description: '',
      vehicleId: vehicleIdInicial ?? '',
      driverId: driverIdInicial ?? '',
      reason: '',
    });
  }, [isOpen, entry, vehicleIdInicial, driverIdInicial]);

  const set = (campo: keyof FormState) => (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) =>
    setForm(f => ({ ...f, [campo]: e.target.value }));

  const validar = (): string | null => {
    if (!form.entryTypeId) return 'Elige el tipo de novedad.';
    if (!form.description.trim()) return 'El relato es obligatorio: el tipo solo clasifica, esto es lo que alguien leerá después.';
    if (!form.occurredAt) return 'Indica cuándo ocurrió.';
    // La asociación la exige el backend con un 400. Se comprueba también aquí
    // para explicar POR QUÉ, que es algo que un 400 genérico no cuenta.
    //
    // ⚠️ **El VIAJE cuenta como asociación**, y al corregir hay que mirar el de
    // la novedad que se está editando: `desdeEntry` no lo trae al formulario
    // porque no se edita, pero existe. Sin esto, una novedad cuya única
    // asociación es su viaje —que el contrato permite— quedaba **imposible de
    // corregir**: la validación saltaba siempre y el `PATCH` no llegaba a
    // salir. Mismo defecto que tenía `ExpenseFormModal`, arreglado igual.
    const asociada = tieneAsociacion({
      vehicleId: form.vehicleId || undefined,
      driverId: form.driverId || undefined,
      tripId: tripIdInicial || entry?.tripId,
    });
    if (!ocultarAsociaciones && !asociada) {
      return 'La novedad tiene que colgar de un vehículo, un conductor o un viaje: una suelta no se puede auditar.';
    }
    if (esCorreccion && !form.reason.trim()) {
      return 'La razón de la corrección es obligatoria — queda en la novedad.';
    }
    return null;
  };

  const enviar = async () => {
    const problema = validar();
    if (problema) { setError(problema); return; }

    setEnviando(true);
    setError(null);
    try {
      if (esCorreccion && entry) {
        const original = desdeEntry(entry);
        const cambios: LogbookEntryPatchDto = { reason: form.reason.trim() };
        const unset: ('vehicleId' | 'driverId' | 'tripId')[] = [];

        if (form.entryTypeId !== original.entryTypeId) cambios.entryTypeId = form.entryTypeId;
        if (form.description.trim() !== original.description) cambios.description = form.description.trim();
        if (form.occurredAt !== original.occurredAt) cambios.occurredAt = fromLocalInputValue(form.occurredAt);
        if (form.vehicleId !== original.vehicleId) {
          if (form.vehicleId) cambios.vehicleId = form.vehicleId;
          else unset.push('vehicleId');
        }
        if (form.driverId !== original.driverId) {
          if (form.driverId) cambios.driverId = form.driverId;
          else unset.push('driverId');
        }
        if (unset.length > 0) cambios.unset = unset;

        // El backend responde 200 sin anotar cuando nada cambió, pero decirlo
        // aquí evita una petición que el usuario leería como "se guardó".
        if (Object.keys(cambios).length === 1) {
          setError('No cambiaste ningún dato de la novedad.');
          setEnviando(false);
          return;
        }
        await onCorregir(entry.id, cambios);
      } else {
        const creada = await onRegistrar({
          entryTypeId: form.entryTypeId,
          occurredAt: fromLocalInputValue(form.occurredAt)!,
          description: form.description.trim(),
          vehicleId: form.vehicleId || undefined,
          driverId: form.driverId || undefined,
          tripId: tripIdInicial || undefined,
        });

        // Las fotos van DESPUÉS y no deshacen nada si fallan: la novedad ya
        // existe y no se puede borrar (ADR-007). Aquí sí se puede decir "ábrela
        // y adjúntala desde su detalle", porque en esta superficie el detalle
        // EXISTE — en el modal del Operador no, y por eso allí el reintento va
        // en sitio.
        if (fotos.length > 0) {
          const { fallidas } = await subir(fotos, creada.id);
          if (fallidas.length > 0) {
            setError(
              `La novedad quedó registrada, pero ${fallidas.length === 1 ? 'una foto no se pudo subir' : `${fallidas.length} fotos no se pudieron subir`}. ` +
              'Ábrela desde el listado y adjúntalas desde su detalle.',
            );
            setEnviando(false);
            return;
          }
        }
      }
      onClose();
    } catch (err) {
      setError(mensajeErrorNovedad(err, esCorreccion ? 'corregir' : 'registrar'));
    } finally {
      setEnviando(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={esCorreccion ? 'Corregir novedad' : 'Registrar novedad'}
      size="md"
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={enviando}>Cancelar</Button>
          <Button onClick={enviar} isLoading={enviando}>
            {esCorreccion ? 'Guardar corrección' : 'Registrar'}
          </Button>
        </>
      }
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.875rem' }}>
        {esCorreccion && (
          <div style={{ display: 'flex', gap: '0.625rem', padding: '0.75rem 0.875rem', background: 'rgba(245,158,11,0.12)', border: '1px solid rgba(245,158,11,0.3)', borderRadius: 'var(--radius-lg)', color: '#F59E0B', fontSize: '12px' }}>
            <AlertTriangle size={16} style={{ flexShrink: 0, marginTop: '1px' }} />
            <span>
              Cambiar la <strong>fecha del hecho</strong> puede sacar esta novedad del expediente de
              un viaje y meterla en el de otro: el expediente se deriva de la ventana, no está
              estampado. La corrección queda en la novedad con el de qué a qué.
            </span>
          </div>
        )}

        {tipos.length === 0 && (
          <div style={{ display: 'flex', gap: '0.625rem', padding: '0.75rem 0.875rem', background: 'rgba(239,68,68,0.12)', border: '1px solid rgba(239,68,68,0.3)', borderRadius: 'var(--radius-lg)', color: '#EF4444', fontSize: '12px' }}>
            <AlertTriangle size={16} style={{ flexShrink: 0, marginTop: '1px' }} />
            <span>El catálogo de tipos está vacío o todos están desactivados. Un Administrador tiene que habilitar al menos uno.</span>
          </div>
        )}

        <Select
          label="Tipo de novedad"
          value={form.entryTypeId}
          onChange={set('entryTypeId')}
          options={[
            { value: '', label: 'Selecciona un tipo…' },
            ...tipos.map(t => ({ value: t.id, label: t.label })),
          ]}
        />

        <Textarea
          label="Relato"
          value={form.description}
          onChange={set('description')}
          rows={3}
          placeholder="Qué pasó, dónde y qué se hizo. Esto es lo que alguien leerá después."
        />

        <Input
          label="Cuándo ocurrió"
          type="datetime-local"
          value={form.occurredAt}
          onChange={set('occurredAt')}
        />
        <p style={{ margin: '-0.5rem 0 0', fontSize: '11px', color: 'var(--text-muted)', display: 'flex', gap: '0.375rem', alignItems: 'flex-start' }}>
          <Info size={12} style={{ flexShrink: 0, marginTop: '1px' }} />
          Cuándo <strong>pasó</strong>, no cuándo lo estás escribiendo. Decide a qué día y a qué viaje pertenece.
        </p>

        {!ocultarAsociaciones && (
          <>
            <Select
              label="Vehículo"
              value={form.vehicleId}
              onChange={set('vehicleId')}
              options={[
                { value: '', label: 'Sin vehículo' },
                ...vehicles.map(v => ({ value: v.id, label: `${v.plate}${v.model ? ` · ${v.model}` : ''}` })),
              ]}
            />

            <Select
              label="Conductor"
              value={form.driverId}
              onChange={set('driverId')}
              options={[
                { value: '', label: 'Sin conductor' },
                ...drivers.map(d => ({ value: d.id, label: d.name })),
              ]}
            />

            <p style={{ margin: '-0.5rem 0 0', fontSize: '11px', color: 'var(--text-muted)' }}>
              Al menos uno de los dos. Una sanción de tránsito puede no tener vehículo, y una varada
              puede no tener conductor identificado.
            </p>
          </>
        )}

        {ocultarAsociaciones && (
          <p style={{ margin: 0, fontSize: '11px', color: 'var(--text-muted)', display: 'flex', gap: '0.375rem', alignItems: 'flex-start' }}>
            <Info size={12} style={{ flexShrink: 0, marginTop: '1px' }} />
            La novedad queda a tu nombre y a tu vehículo asignado — no hace falta elegirlos.
          </p>
        )}

        {tripIdInicial && !esCorreccion && (
          <p style={{ margin: 0, fontSize: '11px', color: 'var(--text-muted)' }}>
            Se asignará a este viaje de forma explícita, así que seguirá en su expediente aunque
            después se corrija la ventana del viaje.
          </p>
        )}

        {esCorreccion && (
          <Input
            label="Razón de la corrección"
            value={form.reason}
            onChange={set('reason')}
            placeholder="El conductor aclaró que fue en el km 40, no en el 14"
          />
        )}

        {/* Solo al registrar, como el recibo en `ExpenseFormModal`. En
            corrección el detalle es el sitio: allí se ven las que ya tiene. */}
        {!esCorreccion && (
          <LogbookPhotoPicker files={fotos} onChange={setFotos} disabled={enviando || subiendo} />
        )}

        {subiendo && progreso && (
          <p style={{ margin: 0, fontSize: '12px', color: 'var(--text-secondary)' }}>
            Subiendo foto {progreso.actual} de {progreso.total}…
          </p>
        )}

        {error && (
          <p style={{ margin: 0, fontSize: '12.5px', color: '#EF4444', fontWeight: 600 }}>{error}</p>
        )}
      </div>
    </Modal>
  );
}
